import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { searchCustomers, searchPartners, searchVendors } from '../api';
import type {
  CustomerSearchResult,
  PartnerSearchResult,
  RecipientType,
  VendorSearchResult,
} from '../types';

export type SearchResultItem =
  | { type: 'CUSTOMER'; item: CustomerSearchResult }
  | { type: 'PARTNER'; item: PartnerSearchResult }
  | { type: 'VENDOR'; item: VendorSearchResult };

async function fetchByType(type: RecipientType, kw: string): Promise<SearchResultItem[]> {
  if (type === 'CUSTOMER') {
    const items = await searchCustomers(kw);
    return items.map((item) => ({ type: 'CUSTOMER' as const, item }));
  }
  if (type === 'PARTNER') {
    const items = await searchPartners(kw);
    return items.map((item) => ({ type: 'PARTNER' as const, item }));
  }
  const items = await searchVendors(kw);
  return items.map((item) => ({ type: 'VENDOR' as const, item }));
}

export function useRecipientSearch(recipientType: RecipientType) {
  const [keyword, setKeyword] = useState('');
  const [debouncedKeyword, setDebouncedKeyword] = useState('');

  useEffect(() => {
    const id = setTimeout(() => setDebouncedKeyword(keyword), 300);
    return () => clearTimeout(id);
  }, [keyword]);

  const { data: results = [], isFetching: isSearching } = useQuery<SearchResultItem[]>({
    queryKey: ['recipient-search', recipientType, debouncedKeyword],
    queryFn: () => fetchByType(recipientType, debouncedKeyword),
    enabled: debouncedKeyword.trim().length > 0,
    staleTime: 30_000,
  });

  function reset() {
    setKeyword('');
    setDebouncedKeyword('');
  }

  return { keyword, setKeyword, results, isSearching, reset };
}
